import { useState } from "react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import GluceraNavigationRoutes from "../../utils/navigationRoutes";
import { useNavigate } from "react-router-dom";
import SessionStorageHelper from "../../utils/sessionStorageHelper";
import { Activity, Calendar } from "lucide-react";

interface AgeData {
  age: number;
  dateOfBirth: string;
}

interface AgepageProps {
  onSubmit?: (data: AgeData) => void;
}

const Agepage = ({ onSubmit }: AgepageProps) => {
  const [dateOfBirth, setDateOfBirth] = useState<string>("");
  const navigate = useNavigate();

  // Calculate age from date of birth
  const calculateAge = (dob: string): number => {
    if (!dob) {
      return 0;
    }
    const today = new Date();
    const birthDate = new Date(dob);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDate.getDate())
    ) {
      age--;
    }
    return age;
  };

  // Format date to dd-mm-yyyy
  const formatDateToDDMMYYYY = (dateStr: string): string => {
    if (!dateStr) {
      return "";
    }
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) {
        return "";
      }
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    } catch (error) {
      return "";
    }
  };

  const calculatedAge = calculateAge(dateOfBirth);

  // Validate date is not in future and is valid
  const isValidDate = dateOfBirth
    ? !isNaN(new Date(dateOfBirth).getTime()) &&
      new Date(dateOfBirth) <= new Date()
    : false;

  const isValidAge =
    dateOfBirth !== "" &&
    isValidDate &&
    calculatedAge >= 13 &&
    calculatedAge <= 100;

  const handleNext = () => {
    if (isValidAge && isValidDate) {
      const formattedDate = formatDateToDDMMYYYY(dateOfBirth);

      // Save date_of_birth for validation (age is calculated from this)
      SessionStorageHelper.saveField("date_of_birth", formattedDate);

      // Store age separately for UI use (not in API data)
      sessionStorage.setItem("calculated_age", calculatedAge.toString());

      if (onSubmit) {
        onSubmit({ age: calculatedAge, dateOfBirth: formattedDate });
      }

      navigate("/glucera" + GluceraNavigationRoutes.HEIGHT_WEIGHT_PAGE);
    }
  };

  return (
    <div className="h-screen w-screen overflow-hidden bg-background">
      <div className="mx-auto flex h-full w-full max-w-md flex-col">
        {/* Header with Progress */}
        <div className="space-y-4 p-4">
          {/* <button
            onClick={handleBack}
            className="w-10 h-10 rounded-full bg-muted flex items-center justify-center hover:bg-muted/80 transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
          </button> */}

          {/* Logo */}
          <div className="flex items-center justify-center gap-2">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-blue-500 to-orange-400">
              <Activity className="h-5 w-5 text-white" />
            </div>
            <span className="text-lg font-semibold text-slate-500">
              Bite Balance
            </span>
          </div>

          {/* Assessment Progress */}
          <p className="text-center text-sm text-muted-foreground">
            Assessment 3 of 12
          </p>
        </div>

        {/* Content */}
        <div className="flex flex-1 flex-col px-4 py-2 sm:px-6 sm:py-4">
          <div className="mb-4 space-y-1 sm:mb-6 sm:space-y-2">
            <h1 className="text-2xl font-bold text-foreground sm:text-3xl">
              What Is Your Date Of Birth?
            </h1>
            <p className="text-xs text-muted-foreground sm:text-sm">
              Your date of birth helps us personalize your diabetes management
              content
            </p>
          </div>

          <div className="mb-6 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="dateOfBirth" className="text-base font-semibold">
                Enter your date of birth
              </Label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 transform text-muted-foreground" />
                <Input
                  id="dateOfBirth"
                  type="date"
                  value={dateOfBirth}
                  onChange={e => setDateOfBirth(e.target.value)}
                  className="h-14 pl-12 text-lg"
                  max={new Date().toISOString().split("T")[0]}
                />
              </div>
            </div>

            {dateOfBirth && !isValidDate && (
              <p className="text-sm text-destructive">
                Please select a date that is not in the future
              </p>
            )}

            {dateOfBirth && isValidDate && calculatedAge < 13 && (
              <p className="text-sm text-destructive">
                You must be at least 13 years old
              </p>
            )}

            {dateOfBirth && isValidDate && calculatedAge > 100 && (
              <p className="text-sm text-destructive">
                Please enter a valid date of birth
              </p>
            )}

            {dateOfBirth && isValidAge && isValidDate && (
              <p className="text-sm font-medium text-red-600 dark:text-red-400">
                ✓ Age calculated: {calculatedAge} years
              </p>
            )}
          </div>

          {/* Button Container */}
          <div className="mt-auto">
            <Button
              onClick={handleNext}
              disabled={!isValidAge || !isValidDate}
              className="h-11 w-full rounded-xl bg-gradient-primary text-sm hover:opacity-90 disabled:opacity-50 sm:h-12 sm:text-base"
            >
              Next
            </Button>
          </div>
        </div>
      </div>

      {/* Safe area padding */}
      <div className="pb-safe p-2" />
    </div>
  );
};

export default Agepage;
